import { useField } from 'react-final-form';
import { SaveImageFn, useUpdateImage } from './useUpdateImage';
import toast from 'react-hot-toast';
import { useStartImageUploadMutation } from 'services/api/upload-vehicle';
import { useFormVehicleId } from './useFormVehicleId';
import axios from 'axios';
import { dataURItoFile, resizeV2 } from 'utils/image';

export const useNonBlockingImageUpload = (level1SectionId: string, imageIndex: number) => {
  const [startUpload] = useStartImageUploadMutation();
  const updateImage = useUpdateImage(level1SectionId, imageIndex);
  const vehicleId = useFormVehicleId();

  const {
    input: { value: sectionId }
  } = useField(`${level1SectionId}.id`);
  const { input: idInput } = useField(`${level1SectionId}.images.${imageIndex}.id`);
  const { input: localUrlInput } = useField(`${level1SectionId}.images.${imageIndex}.local_url`);
  const { input: urlInput } = useField(`${level1SectionId}.images.${imageIndex}.url`);
  const { input: statusInput } = useField(`${level1SectionId}.images.${imageIndex}.status`);
  const {
    input: { value: isDamaged }
  } = useField(`${level1SectionId}.images.${imageIndex}.is_damaged`);
  const {
    input: { value: notes }
  } = useField(`${level1SectionId}.images.${imageIndex}.notes`);
  const {
    input: { value: grade }
  } = useField(`${level1SectionId}.images.${imageIndex}.grade`);

  const {
    input: { value: expirationDate }
  } = useField(`${level1SectionId}.images.${imageIndex}.expiration_date`);

  const fetchSignedUrls = async () => {
    const response = await startUpload({
      vehicleId,
      imageId: idInput.value,
      order: !idInput.value ? imageIndex : undefined,
      sectionId: !idInput.value ? sectionId : undefined
    });

    if ('error' in response) {
      return { success: false };
    }

    return {
      success: true,
      data: response.data
    };
  };

  const getResizedFile = async (currentImageSrc: string | Blob | File, type: 'main' | 'preview') => {
    const size = type === 'main' ? { width: 1600, height: 1200 } : { width: 400, height: 300 };
    let parsedFile;
    try {
      parsedFile = await resizeV2(currentImageSrc, size);
    } catch (error) {
      alert(`Could not resize ${type} image, using original`);
      parsedFile = typeof currentImageSrc === 'string' ? dataURItoFile(currentImageSrc) : parsedFile;
      console.error(error);
      return;
    }

    return parsedFile;
  };

  const uploadToCloud = async (url: string, fileKey: string, file: Blob) => {
    const cancelSource = axios.CancelToken.source();

    await axios.put(url, file, {
      headers: {
        'Content-Type': file.type,
        'x-goog-meta-file_key': fileKey,
        'x-good-meta-signed': 'true'
      },
      onUploadProgress: event => {
        console.log(event, 'on upload progress');
      },
      cancelToken: cancelSource.token
    });

    // set cancel source to storage to later cancel
  };

  return async (currentImageSrc: string | Blob | File) => {
    if (statusInput.value === 'uploading') {
      toast.error('Upload is in progress');
      return;
    }

    statusInput.onChange('uploading');

    try {
      const [mainResized, previewResized] = await Promise.all([
        getResizedFile(currentImageSrc, 'main'),
        getResizedFile(currentImageSrc, 'preview')
      ]);

      localUrlInput.onChange(URL.createObjectURL(mainResized));
      urlInput.onChange(null);

      const response = await fetchSignedUrls();
      if (response.success === false) {
        throw new Error('Failed to fetch signed url');
      }

      const { mainImage, previewImage } = response.data!;

      const { signedUrl: uploadUrl, fileKey, objectUrl } = mainImage;
      const { signedUrl: previewUploadUrl, fileKey: previewFileKey, objectUrl: previewObjectUrl } = previewImage;

      const [mainUpload, previewUpload] = await Promise.allSettled([
        uploadToCloud(uploadUrl, fileKey, mainResized),
        uploadToCloud(previewUploadUrl, previewFileKey, previewResized)
      ]);

      if (mainUpload.status === 'rejected') {
        throw new Error(`Failed to upload. Promise rejected with: ${mainUpload.reason}`);
      }

      const previewFailed = previewUpload.status === 'rejected';
      if (previewFailed) {
        console.info(`Preview failed with reason: ${previewUpload.reason}`);
        console.info(`Using main image as preview...`);
      }

      const payload: Parameters<SaveImageFn>[0] = {};

      payload.url = objectUrl;
      payload.url_thumb = previewFailed ? objectUrl : previewObjectUrl;
      payload.is_damaged = !!isDamaged;
      if (grade) {
        payload.grade = grade;
      }
      if (notes?.message) {
        payload.notes = notes?.message;
      }
      if (expirationDate) {
        payload.expiration_date = expirationDate;
      }

      await updateImage(payload);
      statusInput.onChange('idle');
    } catch (error) {
      console.error('Failed to upload to gcp', error);
      statusInput.onChange('failed');
    }
  };
};
